import { z } from 'zod';

// Money in London Life is GBP held as whole pence, never floating point. One shared definition is used
// by the API, the database layer and both apps, so an amount means the same everywhere (see
// docs ECONOMY_LEDGER.md). The ceiling keeps every value an exact JavaScript integer and is enforced
// again by the database.
export const MAX_PENCE = 9_000_000_000_000_000; // £90 trillion, below Number.MAX_SAFE_INTEGER

// a positive amount a player or admin asks to move; strings, fractions, negatives and huge values fail
export const Pence = z.number().int().positive().max(MAX_PENCE);
export type Pence = z.infer<typeof Pence>;

// "£1,234.56", or "−£5.00" for money going out; the only formatter the apps use, so a receipt can never
// show a different number from the one that moved
const POUNDS = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
export function formatPence(pence: number): string {
  if (!Number.isSafeInteger(pence)) throw new RangeError('pence must be a whole number');
  const text = POUNDS.format(Math.abs(pence) / 100);
  return pence < 0 ? `−${text}` : text;
}

// the system's own accounts: every pound enters or leaves the game through one of these
export const SYSTEM_ACCOUNTS = {
  grants: 'Starting money paid to new lives',
  wages: 'Wages and other earnings',
  shops: 'Purchases from shops, venues and services',
  housing: 'Rent, deposits and house bills',
  transport: 'Fares and travel',
  tax: 'Income tax, National Insurance and council tax',
  adjustments: 'Corrections made by admins',
  closures: 'Money left in a life that was closed',
  fees: 'Fees on money sent between players',
  held: 'Money sent between players, waiting for a check',
  business: "Players' businesses: fit-outs, premises, takings, rent and wages",
  railway: "Players' train companies: registering, trains, takings and the week's bills",
  bank: 'The bank: savings interest and what bonds pay back',
  bookmaker: 'Bets: stakes taken and winnings paid (game money only)',
  government: "City Hall: standing for Mayor and the Mayor's pay",
} as const;
export type SystemAccountKey = keyof typeof SYSTEM_ACCOUNTS;
export const SYSTEM_ACCOUNT_KEYS = Object.keys(SYSTEM_ACCOUNTS) as SystemAccountKey[];

// every kind of money movement; a new kind is added here first, so reports and statements know it
export const LEDGER_TRANSACTION_KINDS = {
  starting_grant: 'Starting money',
  wage: 'Wages',
  purchase: 'Purchase',
  sale: 'Sold back to the shop',
  transfer: 'Transfer',
  rent: 'Rent',
  bill: 'Bill',
  tax: 'Tax',
  fare: 'Fare',
  admin_adjustment: 'Correction by support',
  reversal: 'Reversal',
  life_closed: 'Life closed',
  business_start: 'Started a business',
  takings: 'Business takings',
  business_bills: 'Business bills',
  cash_out: 'Cashed out of a business',
  business_sale: 'Business sold',
  stock: 'Stock for a shop',
  rail_company: 'Train company costs',
  rail_takings: 'Train takings',
  rail_bills: 'Train company bills',
  rail_sale: 'Train sold',
  savings_in: 'Into savings',
  savings_out: 'Out of savings',
  interest: 'Savings interest',
  bond_bought: 'Bond bought',
  bond_paid: 'Bond cashed in',
  moving: 'Moving house',
  home_bought: 'Bought a home',
  home_sold: 'Home sold',
  home_extended: 'Home extension',
  let_bought: 'Bought a home to let',
  let_sold: 'Home to let sold',
  rent_in: 'Rent from tenants',
  bet_placed: 'Bet placed',
  bet_won: 'Bet won',
  treatment: 'Medicine and treatment',
  staff_wages: 'Staff wages',
  car_bought: 'Car bought',
  car_sold: 'Car sold',
  car_service: 'Car service',
  family_held: 'Held for a date, ring or wedding',
  family_paid: 'A date, ring or wedding',
  family_returned: 'A date, ring or wedding: money back',
  baby_fed: 'Fed a baby',
  hustle_held: 'Held for a Hustle job',
  hustle_paid: 'A Hustle job paid',
  hustle_refunded: 'A Hustle job called off: money back',
  hustle_decor: 'New pieces from a decorator',
  election_fee: 'Standing for Mayor',
  mayor_pay: "The Mayor's pay",
} as const;
export type LedgerTransactionKind = keyof typeof LEDGER_TRANSACTION_KINDS;
export const LedgerTransactionKind = z.enum(
  Object.keys(LEDGER_TRANSACTION_KINDS) as [LedgerTransactionKind, ...LedgerTransactionKind[]],
);

export const LedgerAccountStatus = z.enum(['active', 'frozen', 'closed']);
export type LedgerAccountStatus = z.infer<typeof LedgerAccountStatus>;
export const LedgerActorType = z.enum(['player', 'admin', 'system']);
export type LedgerActorType = z.infer<typeof LedgerActorType>;

// every request that moves money sends this header; a repeat with the same key returns the first result
export const IDEMPOTENCY_HEADER = 'idempotency-key';
export const IdempotencyKey = z
  .string()
  .regex(/^[A-Za-z0-9:_-]{16,100}$/, 'Idempotency keys are 16 to 100 letters, digits, colons, dashes or underscores');

// ---- money between players (docs ECONOMY_LEDGER.md, section 5): every transfer says what it is for

export const MONEY_PURPOSES = {
  gift: { label: 'Gift', icon: '🎁' },
  food: { label: 'Food', icon: '🍛' },
  rent: { label: 'Rent', icon: '🏠' },
  bills: { label: 'Bills', icon: '🧾' },
  travel: { label: 'Travel', icon: '🚌' },
} as const;
export type MoneyPurpose = keyof typeof MONEY_PURPOSES;
export const MoneyPurpose = z.enum(Object.keys(MONEY_PURPOSES) as [MoneyPurpose, ...MoneyPurpose[]]);
export const MONEY_NOTE_MAX = 60;
// sent: arrived; held: waiting for a check by the team; returned: the check sent it back
export const TransferStatus = z.enum(['sent', 'held', 'returned']);
export type TransferStatus = z.infer<typeof TransferStatus>;
// open: waiting for the other person; paid, declined, or cancelled by the asker
export const MoneyRequestStatus = z.enum(['open', 'paid', 'declined', 'cancelled']);
export type MoneyRequestStatus = z.infer<typeof MoneyRequestStatus>;
// money in a chat: a card for money sent, or a request for money
export const ChatMoney = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('sent'),
    id: z.string().uuid(),
    amountPence: z.number().int(),
    feePence: z.number().int(),
    purpose: MoneyPurpose,
    note: z.string().nullable(),
    status: TransferStatus,
  }),
  z.object({
    type: z.literal('request'),
    id: z.string().uuid(),
    amountPence: z.number().int(),
    purpose: MoneyPurpose,
    note: z.string().nullable(),
    status: MoneyRequestStatus,
  }),
]);
export type ChatMoney = z.infer<typeof ChatMoney>;
// the fee on money sent between players: a share of it (basis points, 100 = 1%), never under the minimum
export const transferFee = (amountPence: number, settings: { transferFeeBps: number; transferFeeMinPence: number }) =>
  amountPence > 0
    ? Math.max(settings.transferFeeMinPence, Math.round((amountPence * settings.transferFeeBps) / 10_000))
    : 0;

// a meal bought for someone, on its card in the chat (what it was and what it cost when bought)
export const ChatGift = z.object({
  id: z.string().uuid(),
  mealId: z.string(),
  label: z.string(),
  icon: z.string(),
  pricePence: z.number().int(),
  note: z.string().nullable(),
});
export type ChatGift = z.infer<typeof ChatGift>;
